import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';

const FORM_ID = 'MAINTENANCE_SERVICE_ADMINISTRATOR_PRODUCT_LINE_EDIT_FORM';
const PERMISSION_SP = 'SERVICEADMINISTRATORPRODUCTLINE';

function ServiceAdministratorProductLineEdit() {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-product-lines/${pageId}`;

  const preProcessRequestData = data => {
    const {
      status,
      createdByUserId,
      lastModifiedByUserId,
      id,
      industries,
      recordVersion,
      ...other
    } = data;
    return {
      status: status === 'true',
      industries:
        industries?.map(i => {
          return { id: i.value };
        }) || [],
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const { industries, ...other } = data;
    return {
      ...other,
      industries:
        industries?.map(i => {
          return { label: i.industryName, value: i.id };
        }) || [],
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-product-lines/${pageId}`;
    try {
      const response = await get(getPageDataApiUrl);
      if (response?.ok) {
        return {
          data: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        data: null,
        errors: response?.errors || null,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [get, getApiUrl, pageId]);

  const onSubmit = async data => {
    try {
      const requestData = preProcessRequestData(data);
      const response = await put(updateApiUrl, { ...requestData });
      if (response?.ok) {
        snackbar.showMessage(
          formatMessage({
            id: 'SERVICEADMINISTRATORPRODUCTLINE_UPDATE_SUCCESS',
            defaultMessage:
              'Service Administrator Product Line was saved successfully',
          }),
        );
        return {
          nextValues: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEADMINISTRATORPRODUCTLINES_BREADCRUMB',
        defaultMessage: 'Service Administrator Product Lines',
      }),
      icon: IconLibrary.ServiceAdministratorProductLines,
      onClick: () => {
        navigate('/serviceadministratorproductlines');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_SERVICEADMINISTRATORPRODUCTLINE_EDIT_BREADCRUMB',
        defaultMessage: 'Edit Service Administrator Product Line',
      }),
      icon: IconLibrary.ServiceAdministratorProductLine,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_SERVICEADMINISTRATORPRODUCTLINES_STACKABLE_BUTTON',
        defaultMessage: 'Create Service Administrator Product Line',
      }),
      Icon: IconLibrary.ServiceAdministratorProductLine,
      variant: 'outlined',
      onClick: () => navigate('/serviceadministratorproductlines/create'),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_PAGES',
    },
    {
      label: formatMessage({
        id: 'EDIT_SERVICEADMINISTRATORPRODUCTLINES_STACKABLE_BUTTON',
        defaultMessage: 'List Service Administrator Product Lines',
      }),
      Icon: IconLibrary.ServiceAdministratorProductLines,
      variant: 'outlined',
      onClick: () => navigate('/serviceadministratorproductlines'),
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'SERVICEADMINISTRATORPRODUCTLINE_EDIT_HEADING',
          defaultMessage: 'Edit Service Administrator Product Line',
        })}>
        <Box>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={editSchema}
            validationSchema={validationSchema(formatMessage)}
            fetchDefaultValues={fetchDefaultValues}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'SERVICEADMINISTRATORPRODUCTLINE_UPDATE_SAVE_BUTTON',
                  defaultMessage: 'Save Service Administrator Product Line',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'SERVICEADMINISTRATORPRODUCTLINE_UPDATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceAdministratorProductLineEdit);
